import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Nyomkövetés — a ShippyPro egységesített státuszaiból. A futár mindegy:
// PostNL, Spring GDS, ugyanaz a hat fő státusz jön vissza.
// ============================================================================

export type TrackingTone = 'grey' | 'blue' | 'yellow' | 'red' | 'green';

export interface TrackingEventView {
  eventAt: string;
  statusCode: number | null;
  label: string;
  description: string | null;
  location: string | null;
}

export interface TrackingView {
  /** 'main' = a rendelés fő csomagja, 'replacement' = csere csomag */
  kind: 'main' | 'replacement';
  replacementId: number | null;
  /** megjelenítendő megnevezés, pl. „Fő csomag" / „Csere csomag #2 · Törött" */
  title: string;
  trackingCode: string;
  carrierName: string | null;
  trackingUrl: string | null;
  statusCode: number | null;
  statusLabel: string;
  tone: TrackingTone;
  shippedAt: string | null;
  lastEventAt: string | null;
  deliveredAt: string | null;
  lastPolledAt: string | null;
  /** feladás óta eltelt napok */
  daysInTransit: number | null;
  /** a küszöbnél régebben nem érkezett új esemény */
  stalled: boolean;
  stalledDays: number | null;
  /** a legutóbbi lekérdezés hibaüzenete (ha volt) */
  pollError?: string | null;
  /** honnan jön az adat: „AfterShip" / „ShippyPro" (null, ha még nem frissült) */
  source?: string | null;
  events: TrackingEventView[];
}

// ---------------------------------------------------------------------------
// Tracking áttekintő (support felület)
// ---------------------------------------------------------------------------

export type TrackingSeverity = 'green' | 'yellow' | 'red';

export interface TrackingOverviewRow {
  orderId: number;
  receiptId: number | null;
  orderNumber: string | null;
  customer: string;
  countryIso: string | null;
  shopName: string | null;
  orderedAt: string | null;
  /** feladási határidő (Etsy expected_ship_date) */
  shipByAt: string | null;
  kind: 'main' | 'replacement';
  trackingCode: string;
  carrierName: string | null;
  trackingUrl: string | null;
  statusCode: number | null;
  statusLabel: string;
  tone: TrackingTone;
  /** zöld = rendben, sárga = figyelni kell, piros = baj van */
  severity: TrackingSeverity;
  severityReason: string;
  shippedAt: string | null;
  lastEventAt: string | null;
  lastEventLabel: string | null;
  lastEventLocation: string | null;
  deliveredAt: string | null;
  daysInTransit: number | null;
  stalledDays: number | null;
  lateDays: number;
  awaitingPickup: boolean;
  estimatedFrom: string | null;
  estimatedTo: string | null;
  estimateFromActualShipDate: boolean;
  transitScope: string | null;
  source: string | null;
  pollError: string | null;
}

export interface TrackingOverview {
  rows: TrackingOverviewRow[];
  summary: { total: number; green: number; yellow: number; red: number; delivered: number };
  transitRules: TransitRule[];
}

/** Szállítási idő szabály: célország (ISO / 'EU' / '*') → min..max nap. */
export interface TransitRule {
  scope: string;
  min_days: number;
  max_days: number;
}

@Injectable({ providedIn: 'root' })
export class TrackingService {
  constructor(private http: HttpClient) {}

  /** GET tracking/orders/:id — a rendelés küldeményei */
  getForOrder(orderId: number): Observable<TrackingView[]> {
    return this.http.get<TrackingView[]>(`tracking/orders/${orderId}`);
  }

  /**
   * POST tracking/orders/:id/refresh — kézi frissítés a ShippyPro-ból.
   * A szerveren van várakozási idő: ugyanazt a küldeményt percenként
   * legfeljebb egyszer kérdezzük le.
   */
  refreshOrder(orderId: number): Observable<TrackingView[]> {
    return this.http.post<TrackingView[]>(`tracking/orders/${orderId}/refresh`, {});
  }

  /** GET tracking/overview — a támogatás áttekintője (időszak + kereső). */
  getOverview(from: string, to: string, q?: string | null): Observable<TrackingOverview> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (q && q.trim()) {
      params = params.set('q', q.trim());
    }
    return this.http.get<TrackingOverview>('tracking/overview', { params });
  }

  /** POST tracking/refresh-open — minden nyitott küldemény lekérdezése most. */
  refreshOpen(limit = 300): Observable<{ refreshed: number; failed: number }> {
    return this.http.post<{ refreshed: number; failed: number }>(
      'tracking/refresh-open', {}, { params: new HttpParams().set('limit', `${limit}`) },
    );
  }

  getTransitRules(): Observable<TransitRule[]> {
    return this.http.get<TransitRule[]>('tracking/transit-days');
  }

  saveTransitRules(items: TransitRule[]): Observable<TransitRule[]> {
    return this.http.put<TransitRule[]>('tracking/transit-days', { items });
  }

  /**
   * POST etsy/backfill-delivery-estimates — az Etsy szállítási profil szerinti
   * várható érkezés visszatöltése a meglévő rendelésekre (minden engedélyezett
   * bolt). Rendelésenként egy Etsy-hívás, ezért egyszeri művelet.
   */
  backfillEtsyDeliveryEstimates(days = 90): Observable<{ shop: string; checked: number; filled: number; failed: number }[]> {
    return this.http.post<{ shop: string; checked: number; filled: number; failed: number }[]>(
      'etsy/backfill-delivery-estimates', {}, { params: new HttpParams().set('days', `${days}`) },
    );
  }

  /**
   * PATCH orders/:id/tracking — kézzel rögzített küldeménykód (UPS, Foxpost,
   * Spring papíron feladva). A szerver a nyomkövetésbe is felveszi; a futárt
   * üresen hagyva az AfterShip a kódból ismeri fel.
   */
  setManualCode(orderId: number, trackingCode: string, carrierName?: string | null): Observable<unknown> {
    return this.http.patch(`orders/${orderId}/tracking`, {
      tracking_code: trackingCode,
      carrier_name: carrierName || null,
    });
  }
}
